import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { Grid, CircularProgress, Button, Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer, Brush } from 'recharts';
import { blockAnalytics } from '@/graph/bookings';
import styled from 'styled-components';

const ControlsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: flex-end;
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const PresetButton = styled(Button)`
  && {
    border-radius: 16px;
    text-transform: none;
    font-size: 13px;
    padding: 6px 16px;
    margin-right: 8px;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
`;

function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDate(val: string): Date {
  const [y, m, d] = val.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function minutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 10) / 10;
}

type Aggregation = 'daily' | 'weekly' | 'monthly';

interface BlockAnalyticsTrendLineProps {
  date?: Date;
  height?: string | number;
  showBrush?: boolean;
  defaultAggregation?: Aggregation;
  defaultDaysBack?: number;
  onAggregationChange?: (aggregation: Aggregation) => void;
}

export default function BlockAnalyticsTrendLine({
  date = new Date(),
  height = 'calc(100vh - 440px)',
  showBrush = true,
  defaultAggregation = 'daily',
  defaultDaysBack = 30,
  onAggregationChange,
}: BlockAnalyticsTrendLineProps) {
  const [aggregation, setAggregation] = useState<Aggregation>(defaultAggregation);

  const endDefault = new Date();
  const startDefault = new Date();
  startDefault.setDate(endDefault.getDate() - (defaultDaysBack - 1));

  const [startDate, setStartDate] = useState<string>(toDateInputValue(startDefault));
  const [endDate, setEndDate] = useState<string>(toDateInputValue(endDefault));

  const [startDateInput, setStartDateInput] = useState<string>(toDateInputValue(startDefault));
  const [endDateInput, setEndDateInput] = useState<string>(toDateInputValue(endDefault));
  const [isTypingStart, setIsTypingStart] = useState<boolean>(false);
  const [isTypingEnd, setIsTypingEnd] = useState<boolean>(false);
  const [isBrushing, setIsBrushing] = useState<boolean>(false);

  useEffect(() => {
    if (date) {
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);
      const startVal = toDateInputValue(monthStart);
      const endVal = toDateInputValue(monthEnd);
      setStartDate(startVal);
      setEndDate(endVal);
      setStartDateInput(startVal);
      setEndDateInput(endVal);
    }
  }, [date]);

  useEffect(() => {
    if (onAggregationChange) {
      onAggregationChange(aggregation);
    }
  }, [aggregation, onAggregationChange]);

  const variables = useMemo(() => {
    return {
      filter: {
        dateRange: {
          from: format(parseDate(startDate), 'YYYY-MM-DD'),
          to: format(parseDate(endDate), 'YYYY-MM-DD'),
        },
      },
      aggregation,
    };
  }, [startDate, endDate, aggregation]);

  const { data, loading, error } = useQuery(blockAnalytics, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const chartData = useMemo(() => {
    if (!data?.blockAnalytics) return [];

    return data.blockAnalytics.map((point: any) => ({
      label: point.name,
      allocated: minutesToHours(point.allocated || 0),
      scheduled: minutesToHours(point.scheduled || 0),
      actual: minutesToHours(point.used || 0),
    }));
  }, [data]);

  const maxValue = useMemo(() => {
    if (!chartData || chartData.length === 0) return 100;
    const allValues = chartData.flatMap(point => [point.allocated || 0, point.scheduled || 0, point.actual || 0]);
    const max = Math.max(...allValues);
    return Math.ceil(max / 10) * 10;
  }, [chartData]);

  const applyPreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const startVal = toDateInputValue(start);
    const endVal = toDateInputValue(end);
    setStartDate(startVal);
    setEndDate(endVal);
    setStartDateInput(startVal);
    setEndDateInput(endVal);
  };

  const handleStartDateBlur = () => {
    if (startDateInput.length === 10) {
      try {
        const date = parseDate(startDateInput);
        if (!isNaN(date.getTime())) {
          setStartDate(startDateInput);
        } else {
          setStartDateInput(startDate);
        }
      } catch {
        setStartDateInput(startDate);
      }
    }
    setIsTypingStart(false);
  };

  const handleEndDateBlur = () => {
    if (endDateInput.length === 10) {
      try {
        const date = parseDate(endDateInput);
        if (!isNaN(date.getTime())) {
          setEndDate(endDateInput);
        } else {
          setEndDateInput(endDate);
        }
      } catch {
        setEndDateInput(endDate);
      }
    }
    setIsTypingEnd(false);
  };

  const COLORS = {
    scheduled: '#00A7F7',
    actual: '#99E6B3',
    allocated: '#3A4575',
    grid: 'rgba(255,255,255,0.08)',
    axis: 'rgba(255,255,255,0.6)',
  };

  if (loading) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>Error loading block analytics data</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height, width: '100%' }}>
      <ControlsContainer>
        <div>
          <Label>Start Date</Label>
          <DateInput
            type="date"
            value={startDateInput}
            onChange={e => {
              const next = e.target.value;
              setStartDateInput(next);
              if (!isTypingStart && next.length === 10) {
                try {
                  const d = parseDate(next);
                  if (!isNaN(d.getTime())) setStartDate(next);
                } catch {}
              }
            }}
            onBlur={handleStartDateBlur}
            onKeyDown={() => setIsTypingStart(true)}
          />
        </div>
        <div>
          <Label>End Date</Label>
          <DateInput
            type="date"
            value={endDateInput}
            onChange={e => {
              const next = e.target.value;
              setEndDateInput(next);
              if (!isTypingEnd && next.length === 10) {
                try {
                  const d = parseDate(next);
                  if (!isNaN(d.getTime())) setEndDate(next);
                } catch {}
              }
            }}
            onBlur={handleEndDateBlur}
            onKeyDown={() => setIsTypingEnd(true)}
          />
        </div>
        <div>
          <Label>Aggregation</Label>
          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
            <Select
              value={aggregation}
              onChange={e => setAggregation(e.target.value as Aggregation)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                borderRadius: 8,
              }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Label>&nbsp;</Label>
          <div>
            <PresetButton variant="outlined" size="small" onClick={() => applyPreset(5)}>
              Last 5
            </PresetButton>
            <PresetButton variant="outlined" size="small" onClick={() => applyPreset(7)}>
              Last 7
            </PresetButton>
            <PresetButton variant="outlined" size="small" onClick={() => applyPreset(14)}>
              Last 14
            </PresetButton>
            <PresetButton variant="outlined" size="small" onClick={() => applyPreset(30)}>
              Last 30
            </PresetButton>
            <PresetButton variant="outlined" size="small" onClick={() => applyPreset(90)}>
              Last 90
            </PresetButton>
          </div>
        </div>
      </ControlsContainer>

      <div style={{ width: '100%', flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={{ fill: COLORS.axis, fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              interval={Math.max(0, Math.ceil(chartData.length / 10) - 1)}
            />
            <YAxis
              domain={[0, maxValue]}
              tickFormatter={(v: number) => `${v}h`}
              tick={{ fill: COLORS.axis, fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            {!isBrushing && (
              <Tooltip
                formatter={(value: any, name: string) => [`${value} hours`, name]}
                labelFormatter={(label: any) => `${label}`}
                contentStyle={{ background: '#1A2553', border: 0, borderRadius: 6, color: '#fff' }}
                cursor={{ stroke: 'rgba(255,255,255,0.15)' }}
              />
            )}
            <Legend />
            <Line
              type="monotone"
              dataKey="allocated"
              stroke={COLORS.allocated}
              dot={{ r: 3 }}
              strokeWidth={2}
              name="Allocated"
            />
            <Line
              type="monotone"
              dataKey="scheduled"
              stroke={COLORS.scheduled}
              dot={{ r: 4 }}
              strokeWidth={3}
              name="Scheduled"
            />
            <Line
              type="monotone"
              dataKey="actual"
              stroke={COLORS.actual}
              dot={{ r: 4 }}
              strokeWidth={3}
              name="Actual"
            />
            {showBrush && (
              <Brush
                dataKey="label"
                height={30}
                stroke="#00A7F7"
                fill="rgba(255, 255, 255, 0.04)"
                travellerWidth={8}
                tickFormatter={() => ''}
                onChange={() => {
                  if (!isBrushing) {
                    setIsBrushing(true);
                    const handleUp = () => setIsBrushing(false);
                    window.addEventListener('mouseup', handleUp, { once: true });
                    window.addEventListener('touchend', handleUp, { once: true });
                  }
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
